import { useFetch } from '@vueuse/core'
import { AUTH_CONFIG } from 'tlv-auth/src/services/auth/Config'
import { appType, appHeader } from 'tlv-auth/src/comp/AppType'
import {
  buildAuthRequestHeaders,
  createAuthRequestId,
  logAuthEvent,
  sanitizeUrlForLog,
  summarizeErrorForLog
} from '../debug/authDebug'

const API_BASE_URI = () => `${AUTH_CONFIG.authApiUrl}/`

const buildHeaders = (requestId) => ({
  'Content-Type': 'application/json',
  app: appType.value,
  'x-app-code': appHeader.value,
  ...buildAuthRequestHeaders({ requestId })
})

export const transformDataToQueryParameters = (data = {}) => {
  if (Object.keys(data).length === 0) return ''
  return new URLSearchParams(data).toString()
}

// Aligns with findee `packages/findee/src/services/api/_core.js` default request timeout.
const AUTH_API_TIMEOUT_MS = 10_000

const createRequestHandlers = ({ requestId, method, url }) => ({
  beforeFetch({ options }) {
    logAuthEvent('auth.api.request.started', {
      requestId,
      method,
      url: sanitizeUrlForLog(url)
    })

    return { options }
  },
  afterFetch(context) {
    logAuthEvent('auth.api.request.completed', {
      requestId,
      method,
      url: sanitizeUrlForLog(context.response?.url || url),
      statusCode: context.response?.status ?? null,
      ok: context.response?.ok ?? false,
      contentType: context.response?.headers?.get?.('content-type') || ''
    })

    return context
  },
  onFetchError(context) {
    logAuthEvent(
      'auth.api.request.failed',
      {
        requestId,
        method,
        url: sanitizeUrlForLog(context.response?.url || url),
        statusCode: context.response?.status ?? null,
        error: summarizeErrorForLog(context.error)
      },
      'error'
    )

    return context
  }
})

const GET = (apiVersion, path, data = {}) => {
  const requestId = createAuthRequestId()
  const url = `${API_BASE_URI()}${apiVersion}/${path}?${transformDataToQueryParameters(data)}`
  return useFetch(
    url,
    { headers: buildHeaders(requestId) },
    {
      timeout: AUTH_API_TIMEOUT_MS,
      ...createRequestHandlers({
        requestId,
        method: 'GET',
        url
      })
    }
  ).get()
}

const POST = (apiVersion, path, data = {}, { updateDataOnError = false } = {}) => {
  const requestId = createAuthRequestId()
  const url = `${API_BASE_URI()}${apiVersion}/${path}`
  return useFetch(
    url,
    { headers: buildHeaders(requestId) },
    {
      timeout: AUTH_API_TIMEOUT_MS,
      updateDataOnError,
      ...createRequestHandlers({
        requestId,
        method: 'POST',
        url
      })
    }
  ).post(JSON.stringify(data))
}

export const ApiServices = {
  API_BASE_URI,
  GET,
  POST
}
